/**
 * Showcase: a demo shell that lists the catalog games, mounts the selected one in a device frame
 * and opens the operator admin panel per game. URL: /?game=<id>&device=fit|phone&admin=1 (fit = fills the stage, default)
 */
import type { GameCatalog, GameEntry, OperatorSettings, SlotHandle } from '../types';
import { startProbe } from './probe';
import { createSlot } from '../index';
import { loadCatalog, resolveBasePath } from '../config/loader';
import { clearSettings, defaultOperatorSettings, loadSettings, saveSettings, toSlotOptions } from '../platform/operator';
import { openAdmin, toast } from './admin';
import { SHOWCASE_CSS } from './styles';

type Device = 'phone' | 'fit';

const JURISDICTIONS = [
  { id: 'default', name: 'Unregulated / demo' },
  { id: 'uk', name: 'United Kingdom (UKGC)' },
  { id: 'de', name: 'Germany (GlüStV)' },
  { id: 'on', name: 'Ontario (AGCO)' },
  { id: 'nl', name: 'Netherlands (KSA)' },
];
const BRANDS = [
  { id: 'emerald', name: 'Emerald (Lucky Fruit default)' },
  { id: 'ultraviolet', name: 'Ultraviolet' },
  { id: 'lucky-ui', name: 'Theme colours (no tokens)' },
];
const LOCALES = [
  { id: 'en', name: 'English' },
  { id: 'ru', name: 'Русский' },
];

/** ClefDev wordmark (viewBox 0 0 102 10, currentColor) — from the ClefDev UI kit. */
const CLEFDEV_LOGO =
  '<svg viewBox="0 0 102 10" aria-label="ClefDev" role="img"><path d="M13.9729 2.09591H4.89046C4.11732 2.09593 3.45824 2.36839 2.91332 2.91332C2.36839 3.45825 2.09591 4.11731 2.09591 4.89046C2.09591 5.66361 2.36839 6.32267 2.91332 6.8676C3.45824 7.41253 4.11732 7.68499 4.89046 7.68501H13.9729V9.78092H4.89046C3.53979 9.78091 2.38704 9.30356 1.43225 8.3488C0.477435 7.39399 0 6.24117 0 4.89046C2.49851e-06 3.53977 0.477446 2.38706 1.43225 1.43225C2.38705 0.477457 3.53977 1.49184e-05 4.89046 0H13.9729V2.09591Z" fill="currentColor"/><path d="M16.7608 4.89046C16.7608 5.6636 17.0332 6.32267 17.5782 6.8676C18.1231 7.41254 18.7823 7.68501 19.5554 7.68501H28.6378V9.78092H19.5554C18.2047 9.78092 17.0519 9.30359 16.0971 8.3488C15.1423 7.39399 14.6648 6.24117 14.6648 4.89046V0H16.7608V4.89046Z" fill="currentColor"/><path d="M43.3159 2.09591H31.4389V3.84257H42.6172L41.9186 5.93848H31.4389V7.68501H43.3159V9.78092H29.3429V0H43.3159V2.09591Z" fill="currentColor"/><path d="M57.9838 2.09591H46.1068V3.84257H57.9838V5.93848H46.1068V9.78092H44.0108V0H57.9838V2.09591Z" fill="currentColor"/><path fill-rule="evenodd" clip-rule="evenodd" d="M67.7719 0C69.1226 1.3805e-05 70.2753 0.477455 71.2301 1.43225C72.1849 2.38705 72.6624 3.53977 72.6624 4.89046C72.6624 6.24117 72.1849 7.39399 71.2301 8.3488C70.2753 9.30357 69.1226 9.78091 67.7719 9.78092H58.6895V0H67.7719ZM60.7854 7.68501H67.7719C68.545 7.68499 69.2041 7.41253 69.749 6.8676C70.294 6.32267 70.5665 5.66361 70.5665 4.89046C70.5664 4.11731 70.294 3.45825 69.749 2.91332C69.2041 2.36839 68.545 2.09593 67.7719 2.09591H60.7854V7.68501Z" fill="currentColor"/><path d="M87.3303 2.09591H75.4533V3.84257H86.6316L85.933 5.93848H75.4533V7.68501H87.3303V9.78092H73.3574V0H87.3303V2.09591Z" fill="currentColor"/><path d="M95.0205 7.78289L99.5687 0H102L96.278 9.78092H93.756L88.041 0H90.4723L95.0205 7.78289Z" fill="currentColor"/></svg>';

const GEAR =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="3.2"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.09A1.7 1.7 0 0 0 4.6 8.9a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.7 1.7 0 0 0 8.96 4.6 1.7 1.7 0 0 0 10 3.04V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1.03 1.56 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.7 1.7 0 0 0 19.4 9a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.09A1.7 1.7 0 0 0 19.4 15z"/></svg>';

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text != null) n.textContent = text;
  return n;
}

class Showcase {
  private readonly root: HTMLElement;
  private readonly basePath = resolveBasePath();
  private catalog: GameCatalog = { games: [] };
  private current: GameEntry | null = null;
  private slot: SlotHandle | null = null;
  private device: Device;
  private readonly side = el('aside', 'sc-side');
  private readonly stage = el('main', 'sc-stage');
  private readonly games = el('div', 'sc-games');
  private readonly frame = el('div', 'sc-frame');
  private readonly mount = el('div', 'sc-frame__game');
  private readonly segButtons = new Map<Device, HTMLButtonElement>();
  /** Phones and small tablets: compact top bar + the game filling the rest of the viewport, no Fit / Phone choice. */
  private probing = false;
  private readonly mobile = typeof matchMedia === 'function' ? matchMedia('(max-width: 980px)') : null;

  constructor(root: HTMLElement) {
    this.root = root;
    const style = document.createElement('style');
    style.textContent = SHOWCASE_CSS;
    document.head.appendChild(style);
    let device = 'fit';
    try {
      device = new URL(location.href).searchParams.get('device') || localStorage.getItem('showcase:device') || 'fit';
    } catch {
      /* ignore */
    }
    // 'desktop' (older links / saved preference) now means the fitted stage.
    this.device = device === 'phone' ? 'phone' : 'fit';
    this.build();
  }

  private build(): void {
    const app = el('div', 'sc-app');
    // sidebar
    const brand = el('div', 'sc-brand');
    const logo = el('div', 'sc-brand__logo');
    logo.innerHTML = CLEFDEV_LOGO;
    const meta = el('div', 'sc-brand__meta');
    meta.append(el('span', undefined, 'operator demo'));
    brand.append(logo, el('h1', 'sc-brand__title', 'Slot Studio'), meta);
    const seg = el('div', 'sc-seg');
    seg.setAttribute('role', 'group');
    seg.setAttribute('aria-label', 'Viewport');
    for (const d of ['fit', 'phone'] as Device[]) {
      const b = el('button', undefined, d === 'phone' ? 'Phone' : 'Fit');
      b.type = 'button';
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', () => this.setDevice(d));
      seg.appendChild(b);
      this.segButtons.set(d, b);
    }
    this.side.append(brand, el('div', 'sc-label', 'Games'), this.games, el('div', 'sc-label', 'Viewport'), seg);
    // stage
    this.frame.appendChild(this.mount);
    this.stage.append(this.frame);
    app.append(this.side, this.stage);
    this.root.appendChild(app);
    this.applyDevice();
    this.mobile?.addEventListener('change', () => this.applyDevice());
  }

  async init(): Promise<void> {
    try {
      this.catalog = await loadCatalog(this.basePath);
    } catch (err) {
      this.stage.prepend(el('div', 'sc-empty', `Cannot load catalog.json: ${(err as Error).message}`));
      return;
    }
    this.renderGames();
    let id: string | null = null;
    try {
      id = new URL(location.href).searchParams.get('game');
    } catch {
      /* ignore */
    }
    const first = this.catalog.games.find((g) => g.id === id && g.status === 'ready') ?? this.catalog.games.find((g) => g.status === 'ready') ?? null;
    if (!first) {
      this.stage.prepend(el('div', 'sc-empty', 'No ready games in the catalog yet.'));
      return;
    }
    const selected = this.select(first);
    // Deep link to a game's operator panel: /?game=<id>&admin=1 (handy for demos and screenshots).
    let admin = false;
    try {
      admin = new URL(location.href).searchParams.get('admin') === '1';
    } catch {
      /* ignore */
    }
    if (admin) void this.openAdmin(first);
    await selected;
  }

  private renderGames(): void {
    this.games.replaceChildren();
    for (const g of this.catalog.games) {
      const card = el('div', `sc-card${g.status === 'planned' ? ' is-planned' : ''}${this.current?.id === g.id ? ' is-active' : ''}`);
      const main = el('button', 'sc-card__main');
      main.type = 'button';
      main.setAttribute('aria-pressed', String(this.current?.id === g.id));
      main.setAttribute('aria-label', `Open ${g.name}`);
      main.disabled = g.status !== 'ready';
      const thumb = el('div', 'sc-card__thumb');
      if (g.thumbnail) {
        const img = el('img');
        img.src = this.basePath + g.thumbnail;
        img.alt = '';
        thumb.appendChild(img);
      } else thumb.appendChild(el('span', undefined, g.status === 'planned' ? 'TBD' : g.id.slice(0, 3).toUpperCase()));
      const body = el('div', 'sc-card__body');
      body.appendChild(el('div', 'sc-card__name', g.name));
      // Grid / lines / RTP live in the operator panel and the paytable; the card stays visual: artwork + name.
      if (g.status === 'planned') body.appendChild(el('div', 'sc-card__note', 'Coming soon'));
      const admin = el('button', 'sc-card__admin');
      admin.type = 'button';
      admin.title = 'Operator settings';
      admin.setAttribute('aria-label', `Operator settings: ${g.name}`);
      admin.innerHTML = GEAR;
      admin.disabled = g.status !== 'ready';
      admin.addEventListener('click', (e) => {
        e.stopPropagation();
        void this.openAdmin(g);
      });
      main.append(thumb, body);
      card.append(main, admin);
      if (g.status === 'ready') main.addEventListener('click', () => void this.select(g));
      else card.title = g.description ?? 'Coming soon';
      this.games.appendChild(card);
    }
    // Mobile top bar scrolls horizontally: keep the active game's pill in view.
    if (this.mobile?.matches) this.games.querySelector<HTMLElement>('.sc-card.is-active')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }

  private setDevice(d: Device): void {
    this.device = d;
    try {
      localStorage.setItem('showcase:device', d);
    } catch {
      /* ignore */
    }
    this.applyDevice();
  }

  private applyDevice(): void {
    const effective: Device = this.mobile?.matches ? 'fit' : this.device;
    this.frame.className = `sc-frame sc-frame--${effective}`;
    for (const [d, b] of this.segButtons) {
      b.classList.toggle('is-active', d === this.device);
      b.setAttribute('aria-pressed', String(d === this.device));
    }
  }

  async select(entry: GameEntry): Promise<void> {
    this.current = entry;
    this.renderGames();
    try {
      const url = new URL(location.href);
      url.searchParams.set('game', entry.id);
      history.replaceState(null, '', url.href);
    } catch {
      /* ignore */
    }
    await this.remount();
  }

  private async remount(): Promise<void> {
    const entry = this.current;
    if (!entry) return;
    this.slot?.destroy();
    this.slot = null;
    this.mount.replaceChildren();
    const settings = loadSettings(entry.id);
    if (settings.features.sound != null) {
      try {
        localStorage.setItem('simple-slot:sound', settings.features.sound ? '1' : '0');
      } catch {
        /* ignore */
      }
    }
    const params = new URLSearchParams(location.search);
    const options = toSlotOptions(entry, settings, { basePath: this.basePath, storageKey: null, bridge: false, debug: params.get('debug') === '1' });
    const slot = createSlot(this.mount, options);
    if (params.get('debug') === '1' && params.get('probe') === '1' && !this.probing) {
      this.probing = true;
      startProbe(this.mount);
    }
    this.slot = slot;
    try {
      await slot.ready;
    } catch (err) {
      this.mount.replaceChildren(el('div', 'sc-empty', `Failed to start: ${(err as Error).message}`));
    }
  }

  private async openAdmin(entry: GameEntry): Promise<void> {
    openAdmin({
      root: this.root,
      entry,
      settings: loadSettings(entry.id),
      basePath: this.basePath,
      jurisdictions: JURISDICTIONS,
      brands: BRANDS,
      locales: LOCALES,
      onApply: (s) => {
        saveSettings(s);
        toast(this.root, 'Settings saved');
        if (this.current?.id !== entry.id) void this.select(entry);
        else void this.remount();
      },
      onReset: () => {
        clearSettings(entry.id);
        saveSettings(defaultOperatorSettings(entry.id));
        toast(this.root, 'Defaults restored');
        if (this.current?.id === entry.id) void this.remount();
      },
    });
  }
}

const root = document.getElementById('showcase');
if (!root) throw new Error('#showcase missing');
const showcase = new Showcase(root);
void showcase.init();
declare global {
  interface Window {
    showcase: Showcase;
  }
}
window.showcase = showcase;
(window as Window & { SLOT_STUDIO_VERSION?: string }).SLOT_STUDIO_VERSION = __APP_VERSION__;
